import api from "@/plugins/api";
export async function fetchContacts(params, signal) {
    const response = await api.get("/contacts", {
        params: {
            page: params?.page,
            per_page: params?.perPage,
            sortBy: mapSortField(params?.sortBy),
            sortOrder: params?.sortOrder,
            organisationId: params?.organisationId,
            search: params?.search || undefined,
        },
        signal,
    });
    return response.data;
}
export async function fetchContactsByOrganisation(params) {
    const response = await api.get("/contacts", {
        params: {
            page: params.page,
            per_page: params.perPage,
            organisationId: params.organisationId,
            search: params.search || undefined,
            include_ended: params.includeEnded || undefined,
        },
    });
    return response.data;
}
export async function searchContactsByName(search, perPage = 20, signal) {
    const response = await api.get("/contacts", {
        params: { search, per_page: perPage },
        signal,
    });
    return response.data;
}
export async function searchContacts(params) {
    const body = {
        filterId: params?.filterId ?? null,
        ast: params?.ast ?? undefined,
        page: params?.page,
        pageSize: params?.pageSize,
        sortBy: mapSortField(params?.sortBy),
        sortOrder: params?.sortOrder,
    };
    if (params?.refinement !== undefined) {
        body.refinement = params.refinement;
    }
    const response = await api.post("/portal/contacts/search", body);
    return response.data;
}
/**
 * Fetches a single contact aggregate from the BFF.
 *
 * Hits `GET /api/v1/contacts/{id}` (the axios base URL already carries the
 * `/api/v1/` prefix). Passing `unwrapEnvelope: true` is what triggers the response
 * interceptor to unwrap the `ApiResponse<ContactByIdModel>` envelope, so
 * `response.data` is the contact payload directly. A missing contact surfaces
 * as a rejected promise with an axios 404 — callers branch on that for the
 * empty-state.
 */
export async function getContactById(id) {
    const response = await api.get(`/contacts/${id}`, { unwrapEnvelope: true });
    return response.data;
}
/**
 * Updates an existing contact via the BFF.
 *
 * Hits `PUT /api/v1/contacts/{id}` and returns the updated contact id. Passing
 * `unwrapEnvelope: true` unwraps the `ApiResponse<Guid>` envelope so `response.data`
 * is the id directly. Validation (400) / not-found (404) failures surface as a
 * rejected promise the caller turns into an inline error + toast.
 */
export async function updateContact(id, payload) {
    const response = await api.put(`/contacts/${id}`, payload, { unwrapEnvelope: true });
    return response.data;
}
export async function getContactPreview(id) {
    const response = await api.get(`/contacts/${id}/preview`, {
        unwrapEnvelope: true,
    });
    return response.data;
}
export async function createContact(payload) {
    const response = await api.post("/contacts", payload, { unwrapEnvelope: true });
    return response.data;
}
export async function addContactAddress(contactId, payload) {
    await api.post(`/contacts/${contactId}/addresses`, payload, { unwrapEnvelope: true });
}
/**
 * Creates an email communication channel on a contact via the BFF.
 *
 * Hits `POST /api/v1/contacts/{contactId}/communication-channels`. `type` is fixed
 * to `Email` for this US-06 flow. Passing `unwrapEnvelope: true` unwraps the
 * `ApiResponse<Guid>` envelope. Validation (400) / not-found (404) surface as a
 * rejected promise the caller maps to an inline error + toast.
 */
export async function addContactEmail(contactId, payload) {
    const response = await api.post(`/contacts/${contactId}/communication-channels`, { type: "Email", ...payload }, { unwrapEnvelope: true });
    return response.data;
}
/**
 * Updates an email communication channel of a contact via the BFF.
 *
 * Hits `PUT /api/v1/contacts/{contactId}/communication-channels/{channelId}`. Passing
 * `unwrapEnvelope: true` unwraps the `ApiResponse<Guid>` envelope. Validation (400) / not-found (404)
 * surface as a rejected promise the caller maps to an inline error + toast.
 */
export async function updateContactEmail(contactId, channelId, payload) {
    const response = await api.put(`/contacts/${contactId}/communication-channels/${channelId}`, payload, { unwrapEnvelope: true });
    return response.data;
}
/**
 * Lists the members of the shared email group a contact channel belongs to (OP-5804).
 *
 * Returns an empty array when the channel is not shared. Feeds the "Designate a new
 * primary contact" modal shown when the group primary edits its shared email away.
 */
export async function getContactSharedEmailGroup(contactId, channelId) {
    const response = await api.get(`/contacts/${contactId}/communication-channels/${channelId}/shared-group`, { unwrapEnvelope: true });
    return response.data;
}
/**
 * Deletes a communication channel (email, phone) of a contact via the BFF (OP-5610).
 *
 * Hits `DELETE /api/v1/contacts/{contactId}/communication-channels/{channelId}` (live save —
 * the removal is persisted immediately, not staged in the contact form). The BFF returns
 * 204 No Content on success. OPHUB refuses to delete a primary channel and surfaces a
 * 409 Conflict with errorCode `CommunicationChannelCannotBeDeleted`, which propagates here as
 * a rejected promise the caller maps to a localized message.
 */
export async function deleteContactCommunicationChannel(contactId, channelId, newPrimaryChannelId) {
    await api.delete(`/contacts/${contactId}/communication-channels/${channelId}`, {
        unwrapEnvelope: true,
        // OP-5804/US-04d: when the deleted channel is the shared-group primary leaving ≥2
        // members, OPHUB needs the successor on the SAME DELETE (query param) to transfer
        // ownership atomically; omitted otherwise.
        params: newPrimaryChannelId ? { newPrimaryChannelId } : undefined,
    });
}
/**
 * Creates a phone communication channel on a contact via the BFF (OP-5604).
 *
 * Hits `POST /api/v1/contacts/{contactId}/communication-channels` with `type`
 * fixed to `Phone`. Passing `unwrapEnvelope: true` unwraps the `ApiResponse<Guid>`
 * envelope. Validation (400) / not-found (404) surface as a rejected promise the
 * caller maps to an inline error + toast.
 */
export async function addContactPhone(contactId, payload) {
    const response = await api.post(`/contacts/${contactId}/communication-channels`, { type: "Phone", ...payload }, { unwrapEnvelope: true });
    return response.data;
}
/**
 * Updates a phone communication channel of a contact via the BFF (OP-5611).
 *
 * Hits `PUT /api/v1/contacts/{contactId}/communication-channels/{channelId}`. Passing
 * `unwrapEnvelope: true` unwraps the `ApiResponse<Guid>` envelope. Validation (400) / not-found (404)
 * surface as a rejected promise the caller maps to an inline error + toast.
 */
export async function updateContactPhone(contactId, channelId, payload) {
    const response = await api.put(`/contacts/${contactId}/communication-channels/${channelId}`, payload, { unwrapEnvelope: true });
    return response.data;
}
export async function checkContactDuplicates(params, signal) {
    const response = await api.post("/contacts/check-duplicates", params, { unwrapEnvelope: true, signal });
    return response.data.items;
}
export async function getContactDeletionPreview(id) {
    const response = await api.get(`/contacts/${id}/deletion-preview`, {
        unwrapEnvelope: true,
    });
    return response.data;
}
/**
 * Hard-deletes a contact and its non-critical links via the BFF (OP-7197 / OP-7215).
 *
 * Hits `DELETE /api/v1/contacts/{id}`. When the contact is the primary of a shared-email
 * group with 2+ other members, `newPrimaryChannelId` MUST carry the designated successor
 * (409 `CommunicationChannel.SharedEmail.TransferRequired` otherwise); omit it when the
 * group has exactly one other member (auto-dissolve) or the contact owns no shared email.
 * Likewise (OP-7428), when the contact is the principal member of a household with 2+ remaining
 * members, `newHouseholdPrincipalContactId` MUST carry the designated new principal's contact id
 * (409 `Household.Principal.DesignationRequired` otherwise); omit it when the household dissolves
 * (≤1 remaining member, OP-7429) or the contact is not a household principal.
 */
export async function deleteContact(id, newPrimaryChannelId, newHouseholdPrincipalContactId) {
    const params = {};
    if (newPrimaryChannelId)
        params.newPrimaryChannelId = newPrimaryChannelId;
    if (newHouseholdPrincipalContactId)
        params.newHouseholdPrincipalContactId = newHouseholdPrincipalContactId;
    const response = await api.delete(`/contacts/${id}`, {
        unwrapEnvelope: true,
        params: Object.keys(params).length > 0 ? params : undefined,
    });
    return response.data;
}
function mapSortField(field) {
    if (!field)
        return undefined;
    const map = {
        firstName: "first_name",
        lastName: "last_name",
        birthDate: "birth_date",
        createdAt: "created_at",
    };
    return map[field] ?? field;
}
